import {
  LoadingContainer,
  Breadcrumb,
  Panels,
  Bar,
  Tab,
  Tabs,
} from 'peach/components'
import { getName } from 'peach/helpers'
import {
  Switch,
  Route,
  Redirect,
  useRouteMatch,
  useParams,
  useHistory,
} from 'peach/router'

import { Clickable, ConfirmDialog, DynamicForm, Page } from 'core/components'
import {
  useCompanyLoanConfigurationsDelete,
  useCompanyLoanConfigurationsGet,
} from 'core/types'
import {
  InstallmentConfigurationData,
  LOCConfigurationData,
} from 'core/types/generated/loan-types/types'

import DynamicFeeTypes from './FeeTypes/DynamicFeeTypes'
import LoanTypeNotifications from './LoanTypeNotifications'
import {
  installmentLoanTypeSchema,
  lineOfCreditLoanTypeSchema,
} from 'core/utils/generated/formSchemas'

const LoanType = () => {
  const history = useHistory()
  const { url, path } = useRouteMatch()
  const { companyId, loanTypeId } = useParams<{
    companyId?: string
    loanTypeId?: string
  }>()
  const { data, isLoading } = useCompanyLoanConfigurationsGet({
    companyId,
    loanTypeId,
  })

  const loanType = data as InstallmentConfigurationData | LOCConfigurationData

  const { mutateAsync: deleteLoanType } = useCompanyLoanConfigurationsDelete({
    companyId,
    loanTypeId,
  })

  const onDeleteLoanType = async () => {
    await deleteLoanType()
    history.push(`/companies/${companyId}/loan-types`)
  }

  // name is optional on a loan type; assetDisplayName is required, so it beats
  // getName's id fallback.
  const title = loanType?.name
    ? getName(loanType)
    : loanType?.assetDisplayName || getName(loanType)

  const tabs = (
    <Tabs basePath={url}>
      <Tab exact to=''>
        Loan Type
      </Tab>
      <Tab to='/notifications'>Notifications</Tab>
      <Tab to='/fee-types'>Fee Types</Tab>
    </Tabs>
  )

  return (
    <Panels header={<Bar right={tabs} title={title} />}>
      <LoadingContainer isLoading={isLoading}>
        <Breadcrumb title={title} to={url} />

        <Switch>
          <Route exact path={path}>
            <Page variant='full'>
              <DynamicForm
                data={loanType || {}}
                fieldEditMode='toggle'
                fieldOrder={['type', 'name', 'assetDisplayName', 'assetType']}
                otherActions={[
                  <ConfirmDialog
                    key='delete-action'
                    onConfirm={onDeleteLoanType}
                    title='Delete Loan Type'
                  >
                    <Clickable variant='outline'>Delete</Clickable>
                  </ConfirmDialog>,
                ]}
                schema={
                  loanType && loanType?.type === 'installment'
                    ? installmentLoanTypeSchema
                    : lineOfCreditLoanTypeSchema
                }
              />
            </Page>
          </Route>

          <Route crumb path={`${path}/notifications`}>
            <LoanTypeNotifications />
          </Route>

          <Route crumb path={`${path}/fee-types`}>
            <DynamicFeeTypes />
          </Route>

          <Redirect to={url} />
        </Switch>
      </LoadingContainer>
    </Panels>
  )
}

export default LoanType
